import { useTranslate } from '@va/localization';
import {
  fontWeights,
  Paragraph,
  paragraphSizes,
  RadioInputSection,
  SearchInputProps,
  SelectDropdownInputOption,
} from '@va/ui/design-system';
import classNames from 'classnames';
import { ChangeEvent, useEffect, useState } from 'react';
import { SearchField } from '../search-field';

type RadioInputMultiSelectSectionListProps = {
  options: SelectDropdownInputOption[];
  selectedOptions?: SelectDropdownInputOption[];
  onChange: (options: SelectDropdownInputOption[]) => void;
  searchInputProps?: SearchInputProps;
  isLoading?: boolean;
  selectedColor?: string;
  listClassName?: string;
  floating?: boolean;
  hint?: string;
};

export const RadioInputMultiSelectSectionList: React.FC<RadioInputMultiSelectSectionListProps> = ({
  options,
  onChange,
  searchInputProps,
  isLoading,
  selectedOptions,
  listClassName,
  floating,
  hint,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SelectDropdownInputOption[]>(options);

  const translate = useTranslate();

  const defaultOnSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (!searchValue) {
      setFilteredOptions(options);
      return;
    }
    setFilteredOptions(
      options.filter(
        (option) =>
          option.value.toLowerCase().includes(searchValue.toLowerCase()) ||
          option.label.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    );
  }, [options, searchValue]);

  const showSearchField = searchInputProps?.isVisible ?? true;

  return (
    <div
      className={classNames(
        'text-gray-charcoal w-full bg-white rounded-12',
        { 'absolute z-999 shadow-lg': floating },
        listClassName,
      )}
    >
      {showSearchField && (
        <div className='px-3 pt-3 mb-2'>
          <SearchField
            className=''
            clearField={
              searchInputProps?.customClearField ? searchInputProps.customClearField : () => setSearchValue('')
            }
            placeholder={searchInputProps?.placeholder}
            value={searchInputProps?.searchValue ? searchInputProps.searchValue : searchValue}
            onChange={searchInputProps?.customOnSearch ? searchInputProps.customOnSearch : defaultOnSearch}
            data-testid={searchInputProps?.['data-testid']}
          />
        </div>
      )}
      {/* A note about the options reads as nonsense when there are none to describe. */}
      {hint && !!filteredOptions.length && (
        <Paragraph
          colorClassName='text-gray-charcoal'
          className='px-3 pt-1'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
        >
          {hint}
        </Paragraph>
      )}
      <div className='max-h-64 p-3 overflow-auto scrollbar scrollbar-thumb'>
        {filteredOptions.length === 0 && !isLoading && (
          <Paragraph className='p-5'>{translate('select.noOptions')}</Paragraph>
        )}
        {filteredOptions?.map(
          (
            {
              icon,
              label,
              value,
              smallLabel,
              ref,
              disabled,
              infoTooltip,
              labelTooltip,
              info,
              infoProps,
              endIcon,
              className,
              selectedColor,
              'data-testid': dataTestId,
            },
            index,
          ) => {
            return (
              <RadioInputSection
                infoTooltip={infoTooltip}
                titleTooltip={labelTooltip}
                disabled={disabled}
                elementRef={ref}
                key={index}
                className={classNames(
                  'group bg-white hover:bg-white-snow active:bg-gray-concrete',
                  className as string,
                )}
                icon={icon}
                selected={!!selectedOptions?.map((option) => option.value).includes(value)}
                title={label}
                subtitle={smallLabel}
                value={value}
                info={info}
                infoProps={infoProps}
                endIcon={endIcon}
                data-testid={dataTestId}
                onClick={(value: string) => {
                  const option = options.find((option) => option.value === value);
                  if (!option) return;

                  let updatedSelectedOptions: SelectDropdownInputOption[] = selectedOptions ? [...selectedOptions] : [];

                  const alreadySelected = updatedSelectedOptions.find((selected) => selected.value === option.value);
                  if (alreadySelected) {
                    updatedSelectedOptions = updatedSelectedOptions.filter(
                      (selected) => selected.value !== option.value,
                    );
                  } else {
                    updatedSelectedOptions.push(option);
                  }

                  onChange(updatedSelectedOptions);
                }}
                selectedColor={selectedColor}
              />
            );
          },
        )}
        {isLoading && <Paragraph className='text-center py-2'>{translate('label.loading')}...</Paragraph>}
      </div>
    </div>
  );
};
